/**
 * Settings → Bandwidth Thresholds.
 *
 * Edits the Peplink bandwidth-alert thresholds (migration 108). There is one
 * GLOBAL row plus optional per-customer overrides (a customer's override wins
 * over the global default). Backed by:
 *
 *   GET /api/bandwidth/thresholds   list (global + customer rows)
 *   PUT /api/bandwidth/thresholds   upsert one row
 *
 * Validation mirrors the server: daily_gb_alert > 0, spike_ratio_alert > 1.0.
 * Gated by the tab's `users.manage` capability (SettingsPage).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { errMsg } from "../../lib/errMsg";
import { fmtUtc } from "../../lib/format";
import {
  type BandwidthThreshold,
  fetchBandwidthThresholds,
  upsertBandwidthThreshold,
} from "../../services/bandwidthApi";

/** Shared client-side validation — mirrors the server. Returns an error
 *  string or null. */
function validate(dailyGb: number, spikeRatio: number): string | null {
  if (!Number.isFinite(dailyGb) || dailyGb <= 0) {
    return "Daily GB alert must be greater than 0.";
  }
  if (!Number.isFinite(spikeRatio) || spikeRatio <= 1.0) {
    return "Spike ratio must be greater than 1.0.";
  }
  return null;
}

function scopeName(t: BandwidthThreshold): string | null {
  const n = t.customer_name ?? t.scope_name;
  return typeof n === "string" && n ? n : null;
}

export const BandwidthThresholdsTab: React.FC = () => {
  const { token } = useAuth();

  const [rows, setRows] = useState<BandwidthThreshold[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchBandwidthThresholds(token);
      setRows(resp.data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const global = useMemo(
    () => rows.find((r) => r.scope_kind === "global") ?? null,
    [rows],
  );
  const customerRows = useMemo(
    () => rows.filter((r) => r.scope_kind === "customer"),
    [rows],
  );

  const afterSave = async (msg: string) => {
    setNotice(msg);
    setError(null);
    await load();
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}
      {notice && (
        <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2">
          {notice}
        </div>
      )}
      {loading && <div className="text-sm text-secondary-500">Loading…</div>}

      {/* ---- Global default ---- */}
      <section>
        <h3 className="text-sm font-semibold mb-1">Global default</h3>
        <p className="text-[11px] text-secondary-500 mb-3">
          Applies to every Peplink unless a per-customer override below wins.
          An alert fires when a device's daily total exceeds the GB limit, or
          spikes past the ratio × its prior 7-day average.
        </p>
        <ThresholdForm
          key={global ? `global-${global.updated_utc ?? ""}` : "global-new"}
          initialDailyGb={global?.daily_gb_alert}
          initialSpikeRatio={global?.spike_ratio_alert}
          updatedUtc={(global?.updated_utc as string | null) ?? null}
          disabled={!token}
          onSave={async (dailyGb, spikeRatio) => {
            if (!token) return;
            await upsertBandwidthThreshold(token, {
              scope_kind: "global",
              daily_gb_alert: dailyGb,
              spike_ratio_alert: spikeRatio,
            });
            await afterSave("Saved global threshold.");
          }}
        />
      </section>

      {/* ---- Per-customer overrides ---- */}
      <section>
        <h3 className="text-sm font-semibold mb-2">Per-customer overrides</h3>
        {customerRows.length === 0 ? (
          <p className="text-xs text-secondary-500 mb-3">
            No per-customer overrides. Add one below.
          </p>
        ) : (
          <div className="space-y-4 mb-4">
            {customerRows.map((r) => (
              <div
                key={r.scope_id ?? scopeName(r) ?? Math.random()}
                className="border border-secondary-200 rounded-md p-3"
              >
                <div className="text-sm font-medium mb-2">
                  {scopeName(r) ?? "Customer"}{" "}
                  <span className="text-xs text-secondary-500 font-mono">
                    (company_id {r.scope_id ?? "—"})
                  </span>
                </div>
                <ThresholdForm
                  key={`cust-${r.scope_id}-${r.updated_utc ?? ""}`}
                  initialDailyGb={r.daily_gb_alert}
                  initialSpikeRatio={r.spike_ratio_alert}
                  updatedUtc={(r.updated_utc as string | null) ?? null}
                  disabled={!token || r.scope_id == null}
                  onSave={async (dailyGb, spikeRatio) => {
                    if (!token || r.scope_id == null) return;
                    await upsertBandwidthThreshold(token, {
                      scope_kind: "customer",
                      scope_id: r.scope_id,
                      daily_gb_alert: dailyGb,
                      spike_ratio_alert: spikeRatio,
                    });
                    await afterSave(
                      `Saved override for ${scopeName(r) ?? `company ${r.scope_id}`}.`,
                    );
                  }}
                />
              </div>
            ))}
          </div>
        )}

        <AddCustomerOverride
          disabled={!token}
          existingIds={customerRows
            .map((r) => r.scope_id)
            .filter((n): n is number => n != null)}
          onSave={async (companyId, dailyGb, spikeRatio) => {
            if (!token) return;
            await upsertBandwidthThreshold(token, {
              scope_kind: "customer",
              scope_id: companyId,
              daily_gb_alert: dailyGb,
              spike_ratio_alert: spikeRatio,
            });
            await afterSave(`Saved override for company ${companyId}.`);
          }}
        />
      </section>

      <p className="text-[11px] text-secondary-500">
        Thresholds are read fresh each bandwidth-alert cycle, so a change takes
        effect on the next run with no restart.
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared editable form for a single threshold row.
// ---------------------------------------------------------------------------

interface ThresholdFormProps {
  initialDailyGb?: number;
  initialSpikeRatio?: number;
  updatedUtc: string | null;
  disabled?: boolean;
  onSave: (dailyGb: number, spikeRatio: number) => Promise<void>;
}

const ThresholdForm: React.FC<ThresholdFormProps> = ({
  initialDailyGb,
  initialSpikeRatio,
  updatedUtc,
  disabled,
  onSave,
}) => {
  const [dailyGb, setDailyGb] = useState<string>(
    initialDailyGb != null ? String(initialDailyGb) : "",
  );
  const [spikeRatio, setSpikeRatio] = useState<string>(
    initialSpikeRatio != null ? String(initialSpikeRatio) : "",
  );
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const save = async () => {
    const d = Number(dailyGb);
    const s = Number(spikeRatio);
    const err = validate(d, s);
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError(null);
    setBusy(true);
    try {
      await onSave(d, s);
    } catch (e) {
      setLocalError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-xs text-secondary-700">
          <span className="block mb-0.5">Daily GB alert</span>
          <input
            type="number"
            min="0"
            step="0.1"
            inputMode="decimal"
            className="border border-secondary-300 rounded px-2 py-1 text-sm w-32 font-mono"
            value={dailyGb}
            onChange={(e) => setDailyGb(e.target.value)}
            placeholder="e.g. 5"
          />
        </label>
        <label className="text-xs text-secondary-700">
          <span className="block mb-0.5">Spike ratio (× prior 7-day avg)</span>
          <input
            type="number"
            min="1"
            step="0.1"
            inputMode="decimal"
            className="border border-secondary-300 rounded px-2 py-1 text-sm w-32 font-mono"
            value={spikeRatio}
            onChange={(e) => setSpikeRatio(e.target.value)}
            placeholder="e.g. 3"
          />
        </label>
        <Button onClick={() => void save()} disabled={busy || disabled}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
      {localError && (
        <div className="text-xs text-red-700">{localError}</div>
      )}
      {updatedUtc && (
        <div className="text-[11px] text-secondary-500">
          Last updated {fmtUtc(updatedUtc)}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Add-a-new-customer-override form.
// ---------------------------------------------------------------------------

interface AddCustomerOverrideProps {
  disabled?: boolean;
  existingIds: number[];
  onSave: (
    companyId: number,
    dailyGb: number,
    spikeRatio: number,
  ) => Promise<void>;
}

const AddCustomerOverride: React.FC<AddCustomerOverrideProps> = ({
  disabled,
  existingIds,
  onSave,
}) => {
  const [companyId, setCompanyId] = useState<string>("");
  const [dailyGb, setDailyGb] = useState<string>("");
  const [spikeRatio, setSpikeRatio] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const save = async () => {
    const id = Number(companyId);
    if (!Number.isInteger(id) || id <= 0) {
      setLocalError("Enter a valid customer company id.");
      return;
    }
    if (existingIds.includes(id)) {
      setLocalError(
        `Company ${id} already has an override above — edit it there.`,
      );
      return;
    }
    const d = Number(dailyGb);
    const s = Number(spikeRatio);
    const err = validate(d, s);
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError(null);
    setBusy(true);
    try {
      await onSave(id, d, s);
      setCompanyId("");
      setDailyGb("");
      setSpikeRatio("");
    } catch (e) {
      setLocalError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-dashed border-secondary-300 rounded-md p-3 space-y-2">
      <h4 className="text-xs font-semibold">Add customer override</h4>
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-xs text-secondary-700">
          <span className="block mb-0.5">Customer company_id</span>
          <input
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            className="border border-secondary-300 rounded px-2 py-1 text-sm w-40 font-mono"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            placeholder="e.g. 521"
          />
        </label>
        <label className="text-xs text-secondary-700">
          <span className="block mb-0.5">Daily GB alert</span>
          <input
            type="number"
            min="0"
            step="0.1"
            inputMode="decimal"
            className="border border-secondary-300 rounded px-2 py-1 text-sm w-32 font-mono"
            value={dailyGb}
            onChange={(e) => setDailyGb(e.target.value)}
          />
        </label>
        <label className="text-xs text-secondary-700">
          <span className="block mb-0.5">Spike ratio</span>
          <input
            type="number"
            min="1"
            step="0.1"
            inputMode="decimal"
            className="border border-secondary-300 rounded px-2 py-1 text-sm w-32 font-mono"
            value={spikeRatio}
            onChange={(e) => setSpikeRatio(e.target.value)}
          />
        </label>
        <Button onClick={() => void save()} disabled={busy || disabled}>
          {busy ? "Saving…" : "Add override"}
        </Button>
      </div>
      <p className="text-[11px] text-secondary-500">
        The company_id is the customer's platform company id (see Companies).
      </p>
      {localError && <div className="text-xs text-red-700">{localError}</div>}
    </div>
  );
};

export default BandwidthThresholdsTab;
